import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axiosInstance from '../lib/axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Search, CreditCard, Banknote, ChevronDown, Info, Phone, CheckCircle, Shield } from 'lucide-react';
import { formatPrice } from '../lib/utils';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { toast } from 'sonner';
import { trackBeginCheckout, trackOrderPlaced, trackCheckoutError } from '../lib/trackEvent';

// Animated Rangoli Component for Checkout Page
const AnimatedRangoli = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
    {/* Main Central Rangoli - Slow Spinning */}
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] animate-rangoli-spin">
      <svg viewBox="0 0 200 200" className="w-full h-full">
        {/* Outer petals */}
        {[...Array(16)].map((_, i) => (
          <g key={`outer-${i}`} transform={`rotate(${i * 22.5} 100 100)`}>
            <path
              d="M100 15 Q115 50 100 85 Q85 50 100 15"
              fill="#0F5132"
              fillOpacity="0.3"
            />
            <circle cx="100" cy="20" r="3" fill="#FFD700" fillOpacity="0.5" />
          </g>
        ))}
        {/* Middle circle */}
        <circle cx="100" cy="100" r="50" fill="none" stroke="#0F5132" strokeWidth="2" strokeOpacity="0.4" strokeDasharray="8 4" />
        {/* Inner petals */}
        {[...Array(8)].map((_, i) => (
          <g key={`inner-${i}`} transform={`rotate(${i * 45} 100 100)`}>
            <path
              d="M100 55 Q110 75 100 95 Q90 75 100 55"
              fill="#198754"
              fillOpacity="0.4"
            />
          </g>
        ))}
        {/* Center flower */}
        <circle cx="100" cy="100" r="15" fill="#0F5132" fillOpacity="0.3" />
        <circle cx="100" cy="100" r="8" fill="#FFD700" fillOpacity="0.4" />
        {/* Decorative dots */}
        {[...Array(8)].map((_, i) => (
          <circle
            key={`dot-${i}`}
            cx={100 + 65 * Math.cos((i * 45 * Math.PI) / 180)}
            cy={100 + 65 * Math.sin((i * 45 * Math.PI) / 180)}
            r="4"
            fill="#0F5132"
            fillOpacity="0.5"
          />
        ))}
      </svg>
    </div>
    
    {/* Secondary Rangoli - Counter Spinning */}
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] animate-rangoli-spin-reverse opacity-50">
      <svg viewBox="0 0 200 200" className="w-full h-full">
        <circle cx="100" cy="100" r="85" fill="none" stroke="#0F5132" strokeWidth="1" strokeOpacity="0.2" strokeDasharray="4 8" />
        {[...Array(24)].map((_, i) => (
          <circle
            key={`outer-dot-${i}`}
            cx={100 + 90 * Math.cos((i * 15 * Math.PI) / 180)}
            cy={100 + 90 * Math.sin((i * 15 * Math.PI) / 180)}
            r="2"
            fill="#0F5132"
            fillOpacity="0.3"
          />
        ))}
      </svg>
    </div>
    
    {/* Corner Diyas */}
    <div className="absolute top-4 left-4 w-12 h-16 animate-diya-flicker">
      <svg viewBox="0 0 30 40" className="w-full h-full" fill="#FFD700">
        <ellipse cx="15" cy="35" rx="10" ry="3" fillOpacity="0.6" />
        <path d="M8 35 Q8 28 15 24 Q22 28 22 35" fillOpacity="0.5" />
        <ellipse cx="15" cy="18" rx="3" ry="5" fillOpacity="0.4" />
        <path d="M15 13 Q13 8 15 4 Q17 8 15 13" fillOpacity="0.6" />
      </svg>
    </div>
    
    <div className="absolute top-4 right-4 w-12 h-16 animate-diya-flicker" style={{ animationDelay: '0.5s' }}>
      <svg viewBox="0 0 30 40" className="w-full h-full" fill="#FFD700">
        <ellipse cx="15" cy="35" rx="10" ry="3" fillOpacity="0.6" />
        <path d="M8 35 Q8 28 15 24 Q22 28 22 35" fillOpacity="0.5" />
        <ellipse cx="15" cy="18" rx="3" ry="5" fillOpacity="0.4" />
        <path d="M15 13 Q13 8 15 4 Q17 8 15 13" fillOpacity="0.6" />
      </svg>
    </div>
    
    <div className="absolute bottom-4 left-4 w-12 h-16 animate-diya-flicker" style={{ animationDelay: '1s' }}>
      <svg viewBox="0 0 30 40" className="w-full h-full" fill="#FFD700">
        <ellipse cx="15" cy="35" rx="10" ry="3" fillOpacity="0.6" />
        <path d="M8 35 Q8 28 15 24 Q22 28 22 35" fillOpacity="0.5" />
        <ellipse cx="15" cy="18" rx="3" ry="5" fillOpacity="0.4" />
        <path d="M15 13 Q13 8 15 4 Q17 8 15 13" fillOpacity="0.6" />
      </svg>
    </div>
    
    <div className="absolute bottom-4 right-4 w-12 h-16 animate-diya-flicker" style={{ animationDelay: '1.5s' }}>
      <svg viewBox="0 0 30 40" className="w-full h-full" fill="#FFD700">
        <ellipse cx="15" cy="35" rx="10" ry="3" fillOpacity="0.6" />
        <path d="M8 35 Q8 28 15 24 Q22 28 22 35" fillOpacity="0.5" />
        <ellipse cx="15" cy="18" rx="3" ry="5" fillOpacity="0.4" />
        <path d="M15 13 Q13 8 15 4 Q17 8 15 13" fillOpacity="0.6" />
      </svg>
    </div>
    
    {/* Pulsing circles */}
    <div className="absolute top-20 left-20 w-16 h-16 animate-rangoli-pulse">
      <svg viewBox="0 0 50 50" className="w-full h-full">
        <circle cx="25" cy="25" r="20" fill="none" stroke="#0F5132" strokeWidth="1" strokeOpacity="0.3" />
        <circle cx="25" cy="25" r="10" fill="#0F5132" fillOpacity="0.2" />
        {[...Array(6)].map((_, i) => (
          <circle
            key={i}
            cx={25 + 15 * Math.cos((i * 60 * Math.PI) / 180)}
            cy={25 + 15 * Math.sin((i * 60 * Math.PI) / 180)}
            r="2"
            fill="#FFD700"
            fillOpacity="0.4"
          />
        ))}
      </svg>
    </div>
    
    <div className="absolute bottom-20 right-20 w-16 h-16 animate-rangoli-pulse" style={{ animationDelay: '1.5s' }}>
      <svg viewBox="0 0 50 50" className="w-full h-full">
        <circle cx="25" cy="25" r="20" fill="none" stroke="#0F5132" strokeWidth="1" strokeOpacity="0.3" />
        <circle cx="25" cy="25" r="10" fill="#0F5132" fillOpacity="0.2" />
        {[...Array(6)].map((_, i) => (
          <circle
            key={i}
            cx={25 + 15 * Math.cos((i * 60 * Math.PI) / 180)}
            cy={25 + 15 * Math.sin((i * 60 * Math.PI) / 180)}
            r="2"
            fill="#FFD700"
            fillOpacity="0.4"
          />
        ))}
      </svg>
    </div>
  </div>
);

export default function CheckoutPage() {
  const { cart, clearCart } = useCart();
  const { user, loginWithOTP } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [indianStates, setIndianStates] = useState([]);
  const [cashfreeConfig, setCashfreeConfig] = useState(null);
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [statesLoading, setStatesLoading] = useState(true);
  const [shippingData, setShippingData] = useState(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false); // Flag to prevent redirect after order
  
  // OTP Verification States
  const [otpStep, setOtpStep] = useState('phone'); // 'phone', 'otp', 'verified'
  const [otp, setOtp] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(!!user); // If logged in, phone is already verified
  const [verifiedPhone, setVerifiedPhone] = useState(user?.phone || '');
  const [useTestMode, setUseTestMode] = useState(false); // Test mode for development
  
  const [formData, setFormData] = useState({
    // Contact
    email: user?.email || '',
    
    // Delivery
    country: 'India',
    firstName: user?.name?.split(' ')[0] || '',
    lastName: user?.name?.split(' ')[1] || '',
    address: '',
    apartment: '',
    city: '',
    state: 'Tamil Nadu',
    pinCode: '',
    phone: user?.phone || '',
    saveInfo: false,
    
    // Payment
    paymentMethod: 'cashfree', // 'cashfree' or 'cod'
    
    // Billing
    sameAsShipping: true
  });

  const [errors, setErrors] = useState({});
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Calculate totals
  const subtotal = cart.items?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0;
  const shipping = shippingData?.total_shipping || 0;
  const total = subtotal + shipping;
  
  // If user is already logged in, mark phone as verified
  useEffect(() => {
    if (user) {
      setPhoneVerified(true);
      setOtpStep('verified');
      setVerifiedPhone(user.phone);
    }
  }, [user]);

  // Fire begin_checkout event once, when cart has items on this page
  const [checkoutTracked, setCheckoutTracked] = useState(false);
  useEffect(() => {
    if (!checkoutTracked && cart?.items?.length > 0) {
      trackBeginCheckout(cart.total || cart.subtotal || 0);
      setCheckoutTracked(true);
    }
  }, [cart, checkoutTracked]);
  
  // Send OTP for phone verification
  const handleSendOTP = async () => {
    const phone = formData.phone.replace(/\D/g, '');
    if (phone.length !== 10 || !/^[6-9]/.test(phone)) {
      setErrors(prev => ({ ...prev, phone: 'Enter a valid 10-digit phone number' }));
      return;
    }
    
    // Test mode bypass - simulate OTP sent
    if (useTestMode) {
      setOtpStep('otp');
      toast.success('Test Mode: OTP bypassed. Enter any 6 digits.');
      return;
    }
    
    try {
      setOtpSending(true);
      await axiosInstance.post(`${API_URL}/api/auth/send-otp`, {
        phone: `+91${phone}`
      });
      setOtpStep('otp');
      toast.success('OTP sent to your phone number!');
    } catch (error) {
      console.error('Send OTP failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setOtpSending(false);
    }
  };
  
  // Verify OTP and login
  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      toast.error('Please enter a 6-digit OTP');
      return;
    }
    
    // Test mode bypass - verify any 6 digits
    if (useTestMode) {
      setPhoneVerified(true);
      setOtpStep('verified');
      setVerifiedPhone(`+91${formData.phone.replace(/\D/g, '')}`);
      toast.success('Test Mode: Phone verified successfully!');
      return;
    }
    
    try {
      setOtpVerifying(true);
      const phone = `+91${formData.phone.replace(/\D/g, '')}`;
      const response = await axiosInstance.post(`${API_URL}/api/auth/verify-otp`, {
        phone,
        code: otp
      });
      
      // Login the user
      const { access_token, user: userData } = response.data;
      loginWithOTP(access_token, userData);
      
      setPhoneVerified(true);
      setOtpStep('verified');
      setVerifiedPhone(phone);
      
      // Update form with user data if available
      if (userData.name) {
        const nameParts = userData.name.split(' ');
        setFormData(prev => ({
          ...prev,
          firstName: nameParts[0] || prev.firstName,
          lastName: nameParts.slice(1).join(' ') || prev.lastName,
          email: userData.email || prev.email
        }));
      }
      
      // If user already has name/email in form, update their profile
      if (formData.firstName || formData.email) {
        try {
          const profileUpdate = {};
          if (formData.firstName) {
            profileUpdate.name = `${formData.firstName} ${formData.lastName || ''}`.trim();
          }
          if (formData.email) {
            profileUpdate.email = formData.email;
          }
          
          if (Object.keys(profileUpdate).length > 0) {
            await axiosInstance.put(`${API_URL}/api/auth/profile`, profileUpdate, {
              headers: { Authorization: `Bearer ${access_token}` }
            });
          }
        } catch (profileError) {
          console.log('Profile update skipped:', profileError.message);
        }
      }
      
      toast.success('Phone verified successfully! You are now logged in.');
    } catch (error) {
      console.error('Verify OTP failed:', error);
      toast.error(error.response?.data?.detail || 'Invalid OTP. Please try again.');
    } finally {
      setOtpVerifying(false);
    }
  };

  // Fetch Indian states
  useEffect(() => {
    const fetchStates = async () => {
      try {
        setStatesLoading(true);
        const response = await axiosInstance.get(`${API_URL}/api/indian-states`);
        // Ensure response is an array
        if (Array.isArray(response.data)) {
          setIndianStates(response.data);
        } else if (response.data && Array.isArray(response.data.states)) {
          setIndianStates(response.data.states);
        } else {
          // Fallback to default Indian states
          setIndianStates([
            'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
            'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
            'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
            'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
            'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
            'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
            'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
          ]);
        }
      } catch (error) {
        console.error('Failed to fetch states:', error);
        // Use fallback states
        setIndianStates([
          'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
          'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
          'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
          'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
          'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
          'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
          'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
        ]);
      } finally {
        setStatesLoading(false);
      }
    };
    fetchStates();
  }, [API_URL]);

  // Fetch Cashfree config
  useEffect(() => {
    const fetchCashfreeConfig = async () => {
      try {
        const response = await axiosInstance.get(`${API_URL}/api/payment-config/cashfree`);
        setCashfreeConfig(response.data);
        console.log('Cashfree config loaded:', response.data);
      } catch (error) {
        console.error('Failed to fetch Cashfree config:', error);
      }
    };
    fetchCashfreeConfig();
  }, [API_URL]);

  // Initialize Google Maps Autocomplete
  useEffect(() => {
    const initAutocomplete = () => {
      if (!window.google || !window.google.maps || !addressInputRef.current) {
        console.log('Google Maps not loaded yet, retrying...');
        return;
      }

      try {
        const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
          types: ['geocode', 'establishment'], // Show more places - addresses, businesses, landmarks
          componentRestrictions: { country: 'in' },
          fields: ['address_components', 'geometry', 'formatted_address', 'name'] // Get all relevant fields
        });

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          
          if (!place.geometry) {
            console.log('No geometry found for place');
            return;
          }

          // Extract address components
          let street = '';
          let city = '';
          let state = '';
          let pinCode = '';
          let locality = '';
          let sublocality = '';

          place.address_components?.forEach(component => {
            const types = component.types;
            
            if (types.includes('street_number')) {
              street = component.long_name + ' ' + street;
            }
            if (types.includes('route')) {
              street += component.long_name;
            }
            if (types.includes('sublocality_level_1') || types.includes('sublocality_level_2') || types.includes('sublocality')) {
              sublocality = component.long_name;
            }
            if (types.includes('locality')) {
              locality = component.long_name;
            }
            if (types.includes('administrative_area_level_2')) {
              if (!city) city = component.long_name;
            }
            if (types.includes('administrative_area_level_1')) {
              state = component.long_name;
            }
            if (types.includes('postal_code')) {
              pinCode = component.long_name;
            }
          });

          // Use locality or sublocality for city
          city = locality || sublocality || city;

          // Build complete address
          let fullAddress = place.formatted_address || street.trim();
          if (place.name && !fullAddress.includes(place.name)) {
            fullAddress = place.name + ', ' + fullAddress;
          }

          console.log('Address selected:', { fullAddress, city, state, pinCode });

          setFormData(prev => ({
            ...prev,
            address: fullAddress,
            city: city,
            state: state || prev.state,
            pinCode: pinCode,
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng()
          }));

          // Clear errors
          setErrors(prev => ({
            ...prev,
            address: '',
            city: '',
            state: '',
            pinCode: ''
          }));
        });

        autocompleteRef.current = autocomplete;
        console.log('Google Maps Autocomplete initialized successfully');
      } catch (error) {
        console.error('Error initializing autocomplete:', error);
      }
    };

    // Wait for Google Maps to load
    if (window.google && window.google.maps) {
      initAutocomplete();
    } else {
      // Retry after a delay
      const timer = setTimeout(initAutocomplete, 1000);
      return () => clearTimeout(timer);
    }

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, []);

  // Redirect if cart is empty (but not if order was just placed)
  useEffect(() => {
    if (!orderPlaced && (!cart.items || cart.items.length === 0)) {
      navigate('/cart');
    }
  }, [cart.items, navigate, orderPlaced]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  // Calculate shipping rates
  const calculateShipping = async (pincode, state) => {
    if (!cart.items || cart.items.length === 0) {
      setShippingData(null);
      return;
    }

    try {
      setShippingLoading(true);
      const response = await axiosInstance.post(`${API_URL}/api/calculate-shipping`, {
        delivery_pincode: pincode || '',
        destination_state: state || '',
        cart_items: cart.items.map(item => ({
          product_id: item.product_id || item.id,
          quantity: item.quantity
        }))
      });
      
      setShippingData(response.data);
      
      // Show info if multiple vendors
      if (response.data.multiple_vendors) {
        toast.info(`Your items are from ${response.data.vendor_count} different sellers. They will be shipped separately.`);
      }
    } catch (error) {
      console.error('Failed to calculate shipping:', error);
      toast.error('Failed to calculate shipping rates');
      setShippingData(null);
    } finally {
      setShippingLoading(false);
    }
  };

  // Calculate shipping when pincode or state changes
  useEffect(() => {
    const pincode = formData.pincode?.trim();
    const state = formData.state;
    
    // Calculate if we have pincode (6 digits) or state
    if ((pincode && pincode.length === 6) || state) {
      calculateShipping(pincode, state);
    }
  }, [formData.pincode, formData.state, cart.items]);

  const validateForm = () => {
    const newErrors = {};
    
    // Contact validation - if phone is verified, skip phone validation
    if (!phoneVerified && !user) {
      const hasEmail = formData.email.trim() && /\S+@\S+\.\S+/.test(formData.email);
      const hasPhone = formData.phone.trim() && /^[6-9]\d{9}$/.test(formData.phone.replace(/\D/g, ''));
      
      if (!hasEmail && !hasPhone) {
        newErrors.phone = 'Phone number is required for order updates';
      }
    }
    
    // Delivery validation
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.pinCode.trim()) newErrors.pinCode = 'PIN code is required';
    
    // PIN code format validation
    if (formData.pinCode.trim() && !/^\d{6}$/.test(formData.pinCode.trim())) {
      newErrors.pinCode = 'Enter a valid 6-digit PIN code';
    }
    
    // Log errors for debugging
    if (Object.keys(newErrors).length > 0) {
      console.log('Validation errors:', newErrors);
      console.log('Form data:', {
        firstName: formData.firstName,
        lastName: formData.lastName,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        pinCode: formData.pinCode,
        phone: formData.phone,
        phoneVerified: phoneVerified
      });
    }
    
    return newErrors;
  };

  const handlePlaceOrder = async () => {
    // OTP removed — proceed directly.
    
    // Validate form
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Scroll to first error
      const firstError = Object.keys(newErrors)[0];
      document.getElementById(firstError)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      
      // Show specific error message
      const missingFields = Object.keys(newErrors).map(key => {
        const fieldNames = {
          firstName: 'First Name',
          lastName: 'Last Name',
          address: 'Address',
          city: 'City',
          state: 'State',
          pinCode: 'PIN Code',
          phone: 'Phone',
          email: 'Email'
        };
        return fieldNames[key] || key;
      });
      toast.error(`Please fill: ${missingFields.join(', ')}`);
      trackCheckoutError('validation_failed', `Missing: ${missingFields.join(', ')}`);
      return;
    }

    try {
      setLoading(true);
      
      // Prepare order data
      const orderData = {
        items: cart.items.map(item => ({
          product_id: item.product_id || item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          image: item.image || item.images?.[0]
        })),
        shipping_address: {
          street: formData.address,
          apartment: formData.apartment,
          city: formData.city,
          state: formData.state,
          postal_code: formData.pinCode,
          country: formData.country,
          phone: formData.phone,
          latitude: formData.latitude || null,
          longitude: formData.longitude || null
        },
        payment_method: formData.paymentMethod === 'cod' ? 'COD' : 'ONLINE',
        // Guest checkout contact info
        guest_phone: formData.phone,
        guest_email: formData.email || null,
        guest_name: `${formData.firstName} ${formData.lastName}`,
        // Additional fields for payment processing
        customer_email: formData.email,
        customer_name: `${formData.firstName} ${formData.lastName}`,
        total_amount: total,
        shipping_cost: shipping,
        shipping_data: shippingData,
        phone: formData.phone,
        return_url: `${window.location.origin}/payment-success`
      };
      
      // Handle COD
      if (formData.paymentMethod === 'cod') {
        const orderResponse = await axiosInstance.post(`${API_URL}/api/orders`, orderData);
        
        // Update user profile with checkout data (name, email, and address)
        if (user) {
          try {
            const profileUpdate = {};
            const fullName = `${formData.firstName} ${formData.lastName}`.trim();
            if (fullName && fullName !== user.name) {
              profileUpdate.name = fullName;
            }
            if (formData.email && formData.email !== user.email) {
              profileUpdate.email = formData.email;
            }
            
            // Save address to user profile
            profileUpdate.address = {
              street: formData.address,
              apartment: formData.apartment,
              city: formData.city,
              state: formData.state,
              postal_code: formData.pinCode,
              country: formData.country,
              phone: formData.phone
            };
            
            await axiosInstance.put(`${API_URL}/api/auth/profile`, profileUpdate);
          } catch (profileError) {
            console.log('Profile update skipped:', profileError.message);
          }
        }
        
        setOrderPlaced(true); // Prevent redirect to cart
        clearCart();
        toast.success('Order placed successfully!');
        trackOrderPlaced(
          orderResponse.data.total || orderResponse.data.total_amount,
          orderResponse.data.order_number || orderResponse.data.id
        );
        
        // Pass full order data to payment success page
        navigate('/payment-success', { 
          state: { 
            orderId: orderResponse.data.id || orderResponse.data._id,
            order: orderResponse.data
          } 
        });
        return;
      }
      
      // Handle Cashfree payment
      if (formData.paymentMethod === 'cashfree') {
        if (!cashfreeConfig || !cashfreeConfig.is_active) {
          toast.error('Cashfree payment is not configured. Please use COD or contact support.');
          trackCheckoutError('cashfree_not_configured', 'Cashfree payment not configured');
          setLoading(false);
          return;
        }
        
        // Create Cashfree order
        const cashfreeResponse = await axiosInstance.post(`${API_URL}/api/orders/cashfree/create`, orderData);
        
        const { payment_session_id, order_id } = cashfreeResponse.data;
        
        // Load Cashfree SDK
        if (!window.Cashfree) {
          const script = document.createElement('script');
          script.src = cashfreeConfig.environment === 'production' 
            ? 'https://sdk.cashfree.com/js/v3/cashfree.js'
            : 'https://sdk.cashfree.com/js/v3/cashfree.sandbox.js';
          script.async = true;
          document.body.appendChild(script);
          
          // Wait for script to load
          await new Promise((resolve) => {
            script.onload = resolve;
          });
        }
        
        // Initialize Cashfree
        const cashfree = window.Cashfree({
          mode: cashfreeConfig.environment === 'production' ? 'production' : 'sandbox'
        });
        
        // Redirect to payment page
        cashfree.checkout({
          paymentSessionId: payment_session_id,
          returnUrl: `${window.location.origin}/payment-success?order_id=${order_id}`,
          redirectTarget: '_self'
        });
        
        // Clear cart after redirecting to payment
        clearCart();
      }
      
    } catch (error) {
      console.error('Order creation failed:', error);
      const detailRaw = error.response?.data?.detail;
      let msg;
      if (detailRaw) {
        if (Array.isArray(detailRaw)) {
          msg = detailRaw.map(err => `${err.loc?.[err.loc.length - 1] || 'field'}: ${err.msg}`).join(', ');
          toast.error(`Validation error: ${msg}`);
        } else {
          msg = detailRaw;
          toast.error(msg);
        }
      } else {
        msg = 'Failed to create order. Please try again.';
        toast.error(msg);
      }
      trackCheckoutError('order_creation_failed', msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-[#FAFDF8] to-white relative overflow-hidden">
      {/* Animated Rangoli Background */}
      <AnimatedRangoli />
      
      {/* Decorative top border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#0F5132] via-[#198754] to-[#0F5132]" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
          {/* Left Side - Form */}
          <div className="px-6 py-8 lg:px-16 lg:py-12 order-2 lg:order-1 bg-white/80 backdrop-blur-sm">
            {/* Logo */}
            <div className="mb-8">
              <Link to="/" className="flex items-center space-x-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_shopstack-46/artifacts/wfop58ec_image.png" 
                  alt="nammahut" 
                  className="h-10 w-10"
                />
                <div>
                  <span className="text-xl font-bold text-[#0F5132]">nammahut</span>
                  <div className="text-[10px] text-[#198754] -mt-1 tracking-wider">TRADITIONAL MARKETPLACE</div>
                </div>
              </Link>
            </div>

            {/* Breadcrumb */}
            <div className="flex items-center space-x-2 text-sm text-gray-600 mb-8">
              <Link to="/cart" className="hover:text-green-700">Cart</Link>
              <span>&gt;</span>
              <span className="text-gray-900 font-medium">Information</span>
              <span>&gt;</span>
              <span className="text-gray-400">Shipping</span>
              <span>&gt;</span>
              <span className="text-gray-400">Payment</span>
            </div>

            <form className="space-y-8" onSubmit={(e) => { e.preventDefault(); handlePlaceOrder(); }}>
              {/* Contact Section — OTP fully removed */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Phone className="w-5 h-5" />
                    Contact
                  </h2>
                  {!user && (
                    <Link to="/login" className="text-sm text-green-600 hover:underline">
                      Log in
                    </Link>
                  )}
                </div>

                {/* Simple phone input, no OTP */}
                <div className="mb-3">
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium">
                      +91
                    </div>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`h-12 pl-12 ${errors.phone ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Mobile phone number"
                      maxLength={10}
                      data-testid="checkout-phone-input"
                    />
                  </div>
                  {errors.phone && <p className="text-sm text-red-600 mt-1">{errors.phone}</p>}
                </div>

                {/* Email - Optional */}
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`h-12 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Email (optional - for order confirmation)"
                  data-testid="checkout-email-input"
                />
                {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email}</p>}
                
                <div className="flex items-start mt-3">
                  <Checkbox
                    id="emailNews"
                    className="mt-0.5"
                  />
                  <label htmlFor="emailNews" className="ml-2 text-sm text-gray-600">
                    Send me news and offers via SMS/Email
                  </label>
                </div>
              </div>

              {/* Delivery Section */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Delivery</h2>
                
                {/* Country */}
                <div className="mb-4">
                  <Label htmlFor="country">Country/Region</Label>
                  <div className="relative">
                    <select
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      className="w-full h-12 px-4 pr-10 border border-gray-300 rounded-lg bg-white appearance-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="India">India</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* Name Fields */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className={`h-12 ${errors.firstName ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="First name"
                    />
                    {errors.firstName && <p className="text-sm text-red-600 mt-1">{errors.firstName}</p>}
                  </div>
                  <div>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className={`h-12 ${errors.lastName ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Last name"
                    />
                    {errors.lastName && <p className="text-sm text-red-600 mt-1">{errors.lastName}</p>}
                  </div>
                </div>

                {/* Address with Google Autocomplete */}
                <div className="mb-4">
                  <div className="relative">
                    <Input
                      ref={addressInputRef}
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className={`h-12 pl-10 ${errors.address ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Address"
                    />
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  </div>
                  {errors.address && <p className="text-sm text-red-600 mt-1">{errors.address}</p>}
                </div>

                {/* Apartment */}
                <div className="mb-4">
                  <Input
                    id="apartment"
                    name="apartment"
                    value={formData.apartment}
                    onChange={handleInputChange}
                    className="h-12 border-gray-300"
                    placeholder="Apartment, suite, etc. (optional)"
                  />
                </div>

                {/* City, State, PIN */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className={`h-12 ${errors.city ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="City"
                    />
                    {errors.city && <p className="text-sm text-red-600 mt-1">{errors.city}</p>}
                  </div>
                  <div>
                    <div className="relative">
                      <select
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        disabled={statesLoading}
                        className={`w-full h-12 px-4 pr-10 border rounded-lg bg-white appearance-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${errors.state ? 'border-red-500' : 'border-gray-300'}`}
                      >
                        <option value="">State</option>
                        {Array.isArray(indianStates) && indianStates.map(state => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                    </div>
                    {errors.state && <p className="text-sm text-red-600 mt-1">{errors.state}</p>}
                  </div>
                  <div>
                    <Input
                      id="pinCode"
                      name="pinCode"
                      value={formData.pinCode}
                      onChange={handleInputChange}
                      className={`h-12 ${errors.pinCode ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="PIN code"
                      maxLength={6}
                    />
                    {errors.pinCode && <p className="text-sm text-red-600 mt-1">{errors.pinCode}</p>}
                  </div>
                </div>

                {/* Save Info */}
                <div className="flex items-start">
                  <Checkbox
                    id="saveInfo"
                    checked={formData.saveInfo}
                    onCheckedChange={(checked) => handleCheckboxChange('saveInfo', checked)}
                    className="mt-0.5"
                  />
                  <label htmlFor="saveInfo" className="ml-2 text-sm text-gray-600">
                    Save this information for next time
                  </label>
                </div>
              </div>

              {/* Shipping Method */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Shipping method</h2>
                {shippingLoading ? (
                  <div className="p-4 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                      Calculating shipping rates...
                    </div>
                  </div>
                ) : shippingData && shippingData.total_shipping > 0 ? (
                  <div className="p-4 bg-green-50 border border-green-300 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-gray-900">
                        {shippingData.shiprocket_used ? '🚀 Shiprocket Delivery' : '📦 Standard Shipping'}
                      </span>
                      <span className="font-semibold text-green-700">₹{shippingData.total_shipping.toLocaleString('en-IN')}</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      {shippingData.multiple_vendors 
                        ? `Items from ${shippingData.vendor_count} sellers will ship separately.`
                        : 'All items will be shipped together.'
                      }
                    </p>
                    {shippingData.shipping_breakdown && (
                      <div className="mt-3 pt-3 border-t border-green-200 space-y-2">
                        {shippingData.shipping_breakdown.map((breakdown, idx) => (
                          <div key={idx} className="text-sm text-gray-700 bg-white/50 p-2 rounded">
                            <div className="flex justify-between">
                              <span className="font-medium">{breakdown.vendor_name}</span>
                              <span className="text-green-700">₹{breakdown.shipping_cost}</span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {breakdown.items_count} item(s) • {breakdown.total_weight}g • {breakdown.courier_name} • Est. {breakdown.estimated_days} days
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-4 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-600">
                    {formData.pincode?.length === 6 || formData.state 
                      ? 'Shipping rate will be calculated based on your location.' 
                      : 'Enter your pincode or state to view shipping rates.'}
                  </div>
                )}
              </div>

              {/* Payment Section */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment</h2>
                <p className="text-sm text-gray-600 mb-4">All transactions are secure and encrypted.</p>
                
                <RadioGroup value={formData.paymentMethod} onValueChange={(value) => setFormData(prev => ({ ...prev, paymentMethod: value }))}>
                  {/* Cashfree */}
                  <div className={`border rounded-lg ${formData.paymentMethod === 'cashfree' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}>
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center space-x-3">
                        <RadioGroupItem value="cashfree" id="cashfree" />
                        <label htmlFor="cashfree" className="font-medium cursor-pointer">
                          Cashfree Payments (UPI, Cards, Wallets, NetBanking)
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        {/* UPI Logo */}
                        <div className="h-6 w-12 bg-white rounded border flex items-center justify-center">
                          <span className="text-xs font-bold text-green-600">UPI</span>
                        </div>
                        {/* Visa Logo */}
                        <svg className="h-6 w-10" viewBox="0 0 48 32" fill="none">
                          <rect width="48" height="32" rx="4" fill="#1434CB"/>
                          <path d="M19.5 21L21.5 11H24.5L22.5 21H19.5Z" fill="white"/>
                          <path d="M30 11L27.5 18.5L27 16L26.5 12C26.5 12 26.3 11 25 11H20L20 11.2C20 11.2 21.5 11.5 23.5 12.5L26.5 21H29.5L33.5 11H30Z" fill="white"/>
                          <path d="M14.5 11L11.5 18L11 16L9.5 12C9.5 12 9 11 8 11H3V11.2C3 11.2 5 11.6 7 13L9.5 21H13L18.5 11H14.5Z" fill="white"/>
                        </svg>
                        {/* Mastercard Logo */}
                        <svg className="h-6 w-10" viewBox="0 0 48 32" fill="none">
                          <rect width="48" height="32" rx="4" fill="#1A1F71"/>
                          <circle cx="18" cy="16" r="10" fill="#EB001B"/>
                          <circle cx="30" cy="16" r="10" fill="#F79E1B"/>
                          <path d="M24 8C21.5 10 20 13 20 16C20 19 21.5 22 24 24C26.5 22 28 19 28 16C28 13 26.5 10 24 8Z" fill="#FF5F00"/>
                        </svg>
                        {/* RuPay Logo */}
                        <div className="h-6 w-12 bg-white rounded border flex items-center justify-center">
                          <span className="text-xs font-bold text-blue-600">RuPay</span>
                        </div>
                      </div>
                    </div>
                    {formData.paymentMethod === 'cashfree' && (
                      <div className="px-4 pb-4 pt-0">
                        <div className="bg-white border border-gray-200 rounded p-4 flex items-center space-x-3">
                          <CreditCard className="w-12 h-12 text-gray-400" />
                          <p className="text-sm text-gray-600">
                            After clicking "Pay now", you will be redirected to Cashfree Payments to complete your purchase securely.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* COD */}
                  <div className={`border rounded-lg mt-3 ${formData.paymentMethod === 'cod' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}>
                    <div className="flex items-center p-4">
                      <RadioGroupItem value="cod" id="cod" />
                      <label htmlFor="cod" className="ml-3 font-medium cursor-pointer flex items-center">
                        <Banknote className="w-5 h-5 mr-2" />
                        Cash on Delivery (COD)
                      </label>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              {/* Billing Address */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Billing address</h2>
                <RadioGroup value={formData.sameAsShipping ? 'same' : 'different'} onValueChange={(value) => setFormData(prev => ({ ...prev, sameAsShipping: value === 'same' }))}>
                  <div className="flex items-center space-x-2 p-3 border border-gray-300 rounded-lg">
                    <RadioGroupItem value="same" id="same" />
                    <label htmlFor="same" className="cursor-pointer">Same as shipping address</label>
                  </div>
                  <div className="flex items-center space-x-2 p-3 border border-gray-300 rounded-lg mt-3">
                    <RadioGroupItem value="different" id="different" />
                    <label htmlFor="different" className="cursor-pointer">Use a different billing address</label>
                  </div>
                </RadioGroup>
              </div>

              {/* Submit Button - OTP completely removed */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#0F5132] hover:bg-[#198754] text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all"
                data-testid="checkout-pay-btn"
              >
                {loading ? <LoadingSpinner size="sm" /> : 'Pay now'}
              </Button>
            </form>
          </div>

          {/* Right Side - Order Summary */}
          <div className="bg-gradient-to-b from-[#0F5132]/5 to-[#0F5132]/10 px-6 py-8 lg:px-16 lg:py-12 border-l border-[#0F5132]/20 order-1 lg:order-2 relative">
            {/* Traditional corner decorations */}
            <div className="absolute top-4 right-4 w-8 h-8 text-[#0F5132]/20">
              <svg viewBox="0 0 50 50" fill="currentColor">
                <circle cx="10" cy="10" r="3" />
                <path d="M10 5 Q15 10 10 15 Q5 10 10 5" />
                <circle cx="25" cy="5" r="2" />
                <circle cx="5" cy="25" r="2" />
              </svg>
            </div>
            
            <h2 className="text-lg font-semibold mb-6 text-[#0F5132] flex items-center gap-2">
              <span>Order Summary</span>
              <div className="flex-1 h-px bg-gradient-to-r from-[#0F5132]/30 to-transparent"></div>
            </h2>
            
            {/* Cart Items */}
            <div className="space-y-4 mb-6">
              {cart.items?.map((item, index) => (
                <div key={index} className="flex space-x-4">
                  <div className="relative">
                    <img
                      src={item.image || item.images?.[0] || 'https://via.placeholder.com/64'}
                      alt={item.name}
                      className="w-16 h-16 object-cover rounded-lg border"
                    />
                    <span className="absolute -top-2 -right-2 bg-gray-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                      {item.quantity}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900">{item.name}</h3>
                  </div>
                  <div className="text-sm font-medium">
                    ₹{(item.price * item.quantity).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </div>
                </div>
              ))}
            </div>

            {/* Discount Code */}
            <div className="mb-6">
              <div className="flex space-x-2">
                <Input
                  placeholder="Discount code"
                  className="flex-1 h-11 border-gray-300"
                />
                <Button variant="outline" className="h-11 px-6">
                  Apply
                </Button>
              </div>
            </div>

            {/* Totals */}
            <div className="space-y-3 pt-4 border-t">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium">₹{subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 flex items-center">
                  Shipping
                  <Info className="w-4 h-4 ml-1 text-gray-400" />
                </span>
                {shippingLoading ? (
                  <span className="text-gray-500">Calculating...</span>
                ) : shippingData ? (
                  <span className="font-medium">₹{shipping.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                ) : (
                  <span className="text-gray-500">Select state</span>
                )}
              </div>
              
              {/* Shipping Breakdown */}
              {shippingData && shippingData.shipping_breakdown && shippingData.shipping_breakdown.length > 0 && (
                <div className="text-xs text-gray-500 space-y-1 mt-2">
                  {shippingData.shipping_breakdown.map((breakdown, idx) => (
                    <div key={idx} className="flex justify-between">
                      <span>{breakdown.region} ({breakdown.items_count} item{breakdown.items_count > 1 ? 's' : ''}, {breakdown.total_weight}g)</span>
                      <span>₹{breakdown.shipping_cost.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  ))}
                  {shippingData.multiple_orders && (
                    <div className="text-blue-600 font-medium mt-2">
                      * Multiple shipments from different regions
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex justify-between text-lg font-semibold pt-3 border-t">
                <span>Total</span>
                <span className="flex items-center">
                  <span className="text-sm text-gray-500 mr-2">INR</span>
                  ₹{total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
